/**
 * Sub-hook for PO Lines workflow actions: hold, release, accept, decline, critical toggle.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { purchasingAPI, PurchaseOrderLine } from '../../../../lib/api';
import { getApiErrorMessage } from '../../../../lib/api/types/common';
import { useAuth } from '../../../../contexts/AuthContext';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { useUserPermissions } from '../../../../contexts/UserPermissionsContext';
import { usePortalMode } from '../../../../hooks/usePortalMode';
import { NextActionResult } from './types';
import { isFullyInSupplierWarehouse } from './utils';
import { isChangeProposedStatus, canActOnProposal } from '../../pendingProposal';
import { nextActionFor } from '../../nextAction';

interface UsePOLinesActionsParams {
  fetchLines: () => Promise<void>;
  setLines: React.Dispatch<React.SetStateAction<PurchaseOrderLine[]>>;
  bustCacheRef: React.MutableRefObject<boolean>;
}

export function usePOLinesActions({ fetchLines, setLines, bustCacheRef }: UsePOLinesActionsParams) {
  const { user } = useAuth();
  const { isFeatureEnabled } = useMenuSettings();
  const { hasPurchasingWriteAccess } = useUserPermissions();
  // CR-4647: role detection follows the ACTIVE portal mode so dual-mode users who toggle
  // to supplier view get supplier actions, not just raw company_type users.
  // Portal detection aligned with the rest of this feature: POLinesTable uses
  // usePortalMode(), so use the same source here rather than reading
  // useView().portalMode directly. usePortalMode resolves company_type OR
  // platform (either casing) and only defers to the view context for dual-mode
  // users, so single-mode suppliers are classified correctly instead of relying
  // on an exact user.platform === 'SUPPLIER' string match.
  //
  // NOTE: consistency change, not a behaviour fix. For the supplier account that
  // reported Accept/Reject on a change-declined SO line, user.platform IS
  // 'SUPPLIER', so the previous check already resolved to the supplier branch;
  // this refactor does not alter what that line renders.
  const { isSupplier: actingAsSupplier, isCustomer: actingAsCustomer } = usePortalMode();

  // Hold state
  const [selectedLineForHold, setSelectedLineForHold] = useState<PurchaseOrderLine | null>(null);
  const [putOnHoldModalOpen, setPutOnHoldModalOpen] = useState(false);
  const [releaseHoldModalOpen, setReleaseHoldModalOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');

  // Workflow state
  const [selectedLineForWorkflow, setSelectedLineForWorkflow] = useState<PurchaseOrderLine | null>(null);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  // Hold handlers
  const handlePutOnHold = useCallback((line: PurchaseOrderLine) => {
    setSelectedLineForHold(line);
    setHoldReason('');
    setPutOnHoldModalOpen(true);
  }, []);

  const handleReleaseHold = useCallback((line: PurchaseOrderLine) => {
    setSelectedLineForHold(line);
    setReleaseHoldModalOpen(true);
  }, []);

  const submitPutOnHold = useCallback(async () => {
    if (!selectedLineForHold || !holdReason.trim()) {
      toast.error('Please enter a reason for putting this line on hold');
      return;
    }

    try {
      const response = await purchasingAPI.putLineOnHold(selectedLineForHold.id, {
        reason: holdReason
      });

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setPutOnHoldModalOpen(false);
        setSelectedLineForHold(null);
        setHoldReason('');
        bustCacheRef.current = true;
        fetchLines();
      }
    } catch (error: unknown) {
      console.error('Failed to put line on hold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to put line on hold'));
    }
  }, [selectedLineForHold, holdReason, fetchLines, bustCacheRef]);

  const submitReleaseHold = useCallback(async () => {
    if (!selectedLineForHold) return;

    try {
      const response = await purchasingAPI.releaseLineHold(selectedLineForHold.id);

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setReleaseHoldModalOpen(false);
        setSelectedLineForHold(null);
        bustCacheRef.current = true;
        fetchLines();
      }
    } catch (error: unknown) {
      console.error('Failed to release hold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to release hold'));
    }
  }, [selectedLineForHold, fetchLines, bustCacheRef]);

  // Toggle critical flag (CR-3404)
  const handleToggleCritical = useCallback(async (line: PurchaseOrderLine) => {
    try {
      const newCriticalState = !line.is_critical;
      const response = await purchasingAPI.setLineCritical(line.id, newCriticalState);

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setLines(prevLines =>
          prevLines.map(l =>
            l.id === line.id ? { ...l, is_critical: newCriticalState } : l
          )
        );
      }
    } catch (error: unknown) {
      console.error('Failed to toggle critical:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update critical status'));
    }
  }, [setLines]);

  // Workflow action handler
  const handleWorkflowAction = useCallback(async (data: { action: string; lineId: number; comments?: string; reason?: string; proposedDate?: string; proposedQuantity?: number; proposedPrice?: number }) => {
    try {
      let response;

      switch (data.action) {
        case 'accept':
          response = await purchasingAPI.acceptDeliveryDate(data.lineId, {
            comments: data.comments,
          });
          break;
        case 'decline':
          response = await purchasingAPI.declineDeliveryDate(data.lineId, {
            reason: data.reason ?? '',
            comments: data.comments,
          });
          break;
        default:
          throw new Error(`Unknown action: ${data.action}`);
      }

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      setAcceptModalOpen(false);
      setDeclineModalOpen(false);
      setSelectedLineForWorkflow(null);
      bustCacheRef.current = true;
      fetchLines();
    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      toast.error(getApiErrorMessage(error, 'Workflow action failed'));
      throw error;
    }
  }, [fetchLines, bustCacheRef]);

  // Determine if current user should see Accept/Reject action buttons
  // CR-4203: Now also checks role-based site permissions
  const shouldShowActions = useCallback((line: PurchaseOrderLine): boolean => {
    if (!user || !user.platform) return false;
    // CR-4697: No actions on closed (fully received) PO lines
    if (line.status === 'received') return false;
    // Schedule-aware tenants: hide PO-line Accept/Decline — negotiation
    // lives on the Schedule modal only (otherwise two disconnected audit
    // trails for the same logical action).
    if (isFeatureEnabled('po_line_schedules')) return false;

    const status = line.acknowledge_status;
    const isSupplier = actingAsSupplier;
    const isCustomer = !isSupplier && actingAsCustomer;

    // Supplier-side line closure: once the supplier holds enough warehouse
    // stock to cover the full ordered qty, the line is "done" from their
    // side — no more Accept / Decline / Propose. Customer view is unchanged
    // (they still need to GRN it to close the order). Gated on call-off
    // since suppliers without it have no warehouse-allocation concept.
    if (isSupplier && isFeatureEnabled('switch_call_off_tab') && isFullyInSupplierWarehouse(line)) return false;

    // CR-4647: Site-based write access only applies to customer users.
    // Suppliers always have write access to their own PO lines.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    // CR-7277: eligibility below is decided on acknowledge_status alone, so a
    // line whose status claims a pending change while every supplier_proposed_*
    // field is empty was offered Accept here too -- and from the list that is a
    // BULK action, so one click could apply phantom changes across many lines.
    // Accepting one writes supplier_promise_date over the agreed date.
    if (isChangeProposedStatus(status) && !canActOnProposal(line)) return false;

    if (isCustomer) {
      switch (status) {
        case 'change_proposed_supplier':
          return true;
        // Mirror of the supplier rule above: a declined change is resolved, so
        // there is nothing left for the customer to accept or reject either.
        // Proposing a new change is the way forward.
        case 'change_declined':
          return false;
        default:
          return false;
      }
    }

    if (isSupplier) {
      switch (status) {
        case 'new':
          return true;
        case 'change_proposed_customer':
          return true;
        // A declined change is already resolved - the supplier has nothing left
        // to accept or reject. Previously, when the CUSTOMER had proposed the
        // change, the supplier was re-offered Accept/Decline on a line already
        // marked Change Declined (whose Next Action read "Waiting Customer",
        // contradicting the buttons). The route forward is to propose a new
        // change, handled by shouldShowProposeChange.
        case 'change_declined':
          return false;
        default:
          return false;
      }
    }

    return false;
  }, [user, actingAsSupplier, actingAsCustomer, hasPurchasingWriteAccess, isFeatureEnabled]);

  // Determine if decline button should be shown for a line.
  const shouldShowDecline = useCallback((line: PurchaseOrderLine): boolean => {
    if (!shouldShowActions(line)) return false;

    const isSupplierDecline = actingAsSupplier;
    // CR-5892: supplier decline of new PO lines is governed by the single
    // client-controlled "Allow Supplier Decline" flag (Admin Setup → Purchasing).
    // The former "Hide Decline Button" inverse gate was removed so there is one
    // clear setting. allow_supplier_decline is merged per-tenant by the display
    // settings endpoint, so the per-company override is reflected here.
    if (isSupplierDecline && line.acknowledge_status === 'new') {
      return isFeatureEnabled('allow_supplier_decline');
    }

    return true;
  }, [user, actingAsSupplier, shouldShowActions, isFeatureEnabled]);

  // CR-4203: Now also checks role-based site permissions
  const shouldShowProposeChange = useCallback((line: PurchaseOrderLine): boolean => {
    if (!user || !user.platform) return false;
    // CR-4697: No actions on closed (fully received) PO lines
    if (line.status === 'received') return false;
    // Schedule-aware tenants: hide PO-line Propose — negotiation
    // lives on the Schedule modal only.
    if (isFeatureEnabled('po_line_schedules')) return false;

    const status = line.acknowledge_status;
    const isSupplier = actingAsSupplier;
    const isCustomer = !isSupplier && actingAsCustomer;

    // Supplier-side line closure (call-off suppliers only): no Propose
    // Change once the supplier holds enough warehouse stock to cover the
    // full ordered qty.
    if (isSupplier && isFeatureEnabled('switch_call_off_tab') && isFullyInSupplierWarehouse(line)) return false;

    // CR-4647: Site-based write access only applies to customer users.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    if (isCustomer) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        // Propose Change is the only route forward on a declined line,
        // whichever side made the proposal that was declined.
        case 'change_declined':
          return true;
        default:
          return false;
      }
    }

    if (isSupplier) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        // Propose Change is the only route forward on a declined line,
        // whichever side made the proposal that was declined.
        case 'change_declined':
          return true;
        default:
          return false;
      }
    }

    return false;
  }, [user, actingAsSupplier, actingAsCustomer, hasPurchasingWriteAccess, isFeatureEnabled]);

  // Get next action for display (CR-3410). Customer review 11 Sep (item
  // 26b): the rule lives in nextAction.ts so the PO header's Lines panel
  // shows exactly what this column shows.
  const getNextAction = useCallback(
    (line: PurchaseOrderLine): NextActionResult => nextActionFor(line),
    [],
  );

  return {
    // Hold state
    selectedLineForHold,
    setSelectedLineForHold,
    putOnHoldModalOpen,
    setPutOnHoldModalOpen,
    releaseHoldModalOpen,
    setReleaseHoldModalOpen,
    holdReason,
    setHoldReason,

    // Workflow state
    selectedLineForWorkflow,
    setSelectedLineForWorkflow,
    acceptModalOpen,
    setAcceptModalOpen,
    declineModalOpen,
    setDeclineModalOpen,

    // Handlers
    handlePutOnHold,
    handleReleaseHold,
    submitPutOnHold,
    submitReleaseHold,
    handleToggleCritical,
    handleWorkflowAction,
    shouldShowActions,
    shouldShowDecline,
    shouldShowProposeChange,
    getNextAction,
  };
}
